import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig
} from 'axios';
import { resolveApiBaseUrl, resolveApiFallbackUrl, resolveTenantCode } from './envConfig';

const primaryBase = resolveApiBaseUrl();
const fallbackBase = resolveApiFallbackUrl();

// Live-first: start on the primary (live) base; if it's ever unreachable we flip to
// the local fallback for the rest of the session (see the response interceptor below).
let activeBase = primaryBase;

/** The API base currently in use (live or the local fallback). */
export function getActiveApiBase(): string {
  return activeBase;
}

export const api: AxiosInstance = axios.create({
  baseURL: activeBase,
  headers: { 'Content-Type': 'application/json' }
});

// Attach tenant + auth headers on every request.
let tokenGetter: () => string | null = () => null;

export function setTokenGetter(fn: () => string | null): void {
  tokenGetter = fn;
}

api.interceptors.request.use((config) => {
  config.baseURL = activeBase; // honor a session-level fallback switch
  config.headers.set('X-Tenant', resolveTenantCode());
  const token = tokenGetter();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

// Local-fallback: a request that fails with NO HTTP response (network error / host
// offline / CORS-blocked) against the live host switches the whole session to the
// local base and retries once. HTTP errors (4xx/5xx) pass through untouched.
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error?.config as
      | (InternalAxiosRequestConfig & { _fellBack?: boolean })
      | undefined;
    const noResponse = !error?.response;
    const canFallBack =
      noResponse && cfg && !cfg._fellBack && activeBase !== fallbackBase && fallbackBase !== primaryBase;
    if (canFallBack) {
      activeBase = fallbackBase;
      cfg._fellBack = true;
      cfg.baseURL = fallbackBase;
      return api.request(cfg);
    }
    return Promise.reject(error);
  }
);

/**
 * The API's own explanation of a failure, or `fallback` when it didn't give one.
 *
 * Worth reaching for whenever a screen would otherwise say "…try again": the API answers a
 * rejected request with a specific, user-ready `message` (and, for a validation failure, a
 * `result` keyed by field). Swallowing it turned "Table must be a table code, a scanned table
 * QR, or a bill number" into "Could not request the bill, try again" — advice that sent the
 * customer round the same failure indefinitely and told support nothing.
 */
export function apiMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { message?: string; result?: unknown } } })?.response?.data;
  const message = data?.message?.trim();
  if (!message) return fallback;

  // A validation failure's message is a count ("2 fields need attention."); the useful text is
  // in the per-field lists underneath it.
  const fields = data?.result;
  if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
    const first = Object.values(fields as Record<string, unknown>)
      .flatMap((v) => (Array.isArray(v) ? v : [v]))
      .find((v): v is string => typeof v === 'string' && v.trim().length > 0);
    if (first) return first;
  }
  return message;
}
